import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Image,
  Pressable,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { useResponsive } from '@/hooks/common/useResponsive';
import { ResioLogo } from '@/assets/ResioLogo';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { useAppSelector } from '@/store';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';
import { useToast } from '@/contexts/admin/ToastContext';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';

type AccountVerificationRouteProp = RouteProp<AuthStackParamList, 'AccountVerification'>;
type AccountVerificationNavigationProp = StackNavigationProp<AuthStackParamList, 'AccountVerification'>;

// Import native version for mobile viewport on web
const AccountVerificationScreenNative = require('./AccountVerificationScreen.native').AccountVerificationScreen;

export const AccountVerificationScreen: React.FC = (props) => {
  const { layoutMode } = useResponsive();
  
  // On mobile/tablet viewports, show native component
  if (layoutMode !== 'desktop') {
    return <AccountVerificationScreenNative {...props} />;
  }
  
  // Desktop viewport - render the desktop web UI below
  return <AccountVerificationScreenDesktop {...props} />;
};

// Rename the original component to Desktop version
const AccountVerificationScreenDesktop: React.FC = () => {
  const styles = useThemedStyles(makeStyles);
  const navigation = useNavigation<AccountVerificationNavigationProp>();
  const route = useRoute<AccountVerificationRouteProp>();
  const { isMobile, isDesktop } = useResponsive();
  const { semantic, components } = useThemeColors();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  
  // Get admin mode from auth state
  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;
  
  const [isResending, setIsResending] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [resendHovered, setResendHovered] = useState(false);
  
  const email = route.params?.email || '';
  const isSignup = route.params?.isSignup || false;
  
  // Toast notifications
  const { showSuccess, showError } = useToast();

  const handleResendEmail = async () => {
    if (!email) {
      showError('No email address found.');
      return;
    }

    setIsResending(true);
    try {
      // Use appropriate API based on mode and flow type with targetPath (matching web apps)
      let response;
      const targetPath = '/reset-password'; // Matching web apps emailRedirectPath
      
      if (isSignup) {
        // For signup flow, use sendVerification with targetPath
        response = await AuthAPI.sendVerification(email, targetPath);
      } else {
        // For forgot password flow, use resident forgot password API with targetPath
        response = await AuthAPI.forgotPassword(email, targetPath);
      }
      
      if (response.success) {
        // Show success toast matching web apps behavior
        showSuccess('Verification email successfully sent');
      } else {
        // Use web apps specific error message for sendVerificationEmail
        showError(response.message || 'We were unable to process your request at this time. Please try again or contact support');
      }
    } catch (error: any) {
      // Use web apps specific error message for sendVerificationEmail
      showError(error.message || 'We were unable to process your request at this time. Please try again or contact support');
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to login screen (matching web apps)
    navigation.navigate('Login');
  };

  // Dynamic styles based on responsive breakpoints and admin mode
  const dynamicStyles = {
    pageWrapper: [
      styles.pageWrapper,
      isAdminMode ? styles.pageWrapperAdmin : styles.pageWrapperResident,
    ],
    splitContainer: [
      styles.splitContainer,
      isMobile && styles.splitContainerMobile,
      isDesktop && styles.splitContainerDesktop,
    ],
    imagePane: [
      styles.imagePane,
      isMobile && styles.imagePaneMobile,
      isDesktop && styles.imagePaneDesktop,
    ],
    formPane: [
      styles.formPane,
      isAdminMode ? styles.formPaneAdmin : styles.formPaneResident,
      { backgroundColor: semantic.background.primary },
      isMobile && styles.formPaneMobile,
      isDesktop && styles.formPaneDesktop,
    ],
    formInner: [
      styles.formInner,
      isMobile && styles.formInnerMobile,
      isDesktop && styles.formInnerDesktop,
    ],
    title: [
      styles.title,
      isAdminMode ? styles.titleAdmin : styles.titleResident,
      { color: semantic.text.primary },
      isMobile && styles.titleMobile,
      isDesktop && styles.titleDesktop,
    ],
  };

  return (
    <View style={dynamicStyles.pageWrapper}>
      <AuthHeader show={true} />

      <View style={dynamicStyles.splitContainer}>
        {/* Left side - Image pane (hidden on mobile, visible on desktop) */}
        <View style={dynamicStyles.imagePane}>
          <Image
            source={isAdminMode ? require('@/assets/images/login.png') : require('@/assets/images/sign-up.png')}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
        
        {/* Right side - Form pane */}
        <View style={dynamicStyles.formPane}>
          <View style={dynamicStyles.formInner}>
            {/* Title - matching CheckEmail component */}
            <Text style={dynamicStyles.title}>Account verification</Text>
            
            {/* Description - matching CheckEmail component */}
            <Text style={styles.description}>
              Please check your email to verify your account to proceed with setting your password.
            </Text>

            {/* Button Container - matching CheckEmail component layout */}
            <View style={styles.buttonContainer}>
              <Pressable
                style={({ pressed }) => [
                  styles.cancelButton,
                  { borderColor: components.button.outlineBorder },
                  Platform.OS === 'web' && cancelHovered && styles.cancelButtonHovered,
                  Platform.OS === 'web' && cancelHovered && { backgroundColor: semantic.background.secondary },
                  Platform.OS === 'web' && pressed && styles.cancelButtonPressed,
                  Platform.OS === 'web' && pressed && { backgroundColor: semantic.background.tertiary },
                ]}
                onPress={handleCancel}
                disabled={isResending}
                onHoverIn={Platform.OS === 'web' ? () => setCancelHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setCancelHovered(false) : undefined}
              >
                <Text style={[
                  styles.cancelButtonText,
                  { color: components.button.outlineText },
                  Platform.OS === 'web' && cancelHovered && styles.cancelButtonTextHovered,
                  Platform.OS === 'web' && cancelHovered && { color: semantic.text.primary },
                ]}>
                  Cancel
                </Text>
              </Pressable>

              <Pressable
                style={({ pressed }) => [
                  styles.resendButton,
                  { borderColor: components.button.outlineBorder },
                  isResending && styles.resendButtonDisabled,
                  Platform.OS === 'web' && resendHovered && !isResending && styles.resendButtonHovered,
                  Platform.OS === 'web' && resendHovered && !isResending && { backgroundColor: semantic.background.secondary },
                  Platform.OS === 'web' && pressed && !isResending && styles.resendButtonPressed,
                  Platform.OS === 'web' && pressed && !isResending && { backgroundColor: semantic.background.tertiary },
                ]}
                onPress={handleResendEmail}
                disabled={isResending}
                onHoverIn={Platform.OS === 'web' ? () => setResendHovered(true) : undefined}
                onHoverOut={Platform.OS === 'web' ? () => setResendHovered(false) : undefined}
              >
                {/* {isResending ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : ( */}
                  <Text style={[
                    styles.resendButtonText,
                    { color: components.button.outlineText },
                    Platform.OS === 'web' && resendHovered && !isResending && styles.resendButtonTextHovered,
                    Platform.OS === 'web' && resendHovered && !isResending && { color: semantic.text.primary },
                  ]}>
                    Resend
                  </Text>
                {/* )} */}
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

export const AccountVerificationDesktopContent: React.FC = () => {
  const styles = useThemedStyles(makeStyles);
  const navigation = useNavigation<AccountVerificationNavigationProp>();
  const route = useRoute<AccountVerificationRouteProp>();
  const { semantic, components } = useThemeColors();

  const { user } = useAppSelector((state) => state.auth);
  const isAdminMode = user?.roles && user.roles.length > 0;

  const [isResending, setIsResending] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [resendHovered, setResendHovered] = useState(false);

  const email = route.params?.email || '';
  const isSignup = route.params?.isSignup || false;

  const { showSuccess, showError } = useToast();

  const handleResendEmail = async () => {
    if (!email) {
      showError('No email address found.');
      return;
    }
    setIsResending(true);
    try {
      let response;
      const targetPath = '/reset-password';
      if (isSignup) {
        response = await AuthAPI.sendVerification(email, targetPath);
      } else {
        response = await AuthAPI.forgotPassword(email, targetPath);
      }
      if (response.success) {
        showSuccess('Verification email successfully sent');
      } else {
        showError(response.message || 'We were unable to process your request at this time. Please try again or contact support');
      }
    } catch (error: any) {
      showError(error.message || 'We were unable to process your request at this time. Please try again or contact support');
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = () => {
    navigation.navigate('Login');
  };

  const titleStyle = [styles.title, styles.titleDesktop, isAdminMode ? styles.titleAdmin : styles.titleResident, { color: semantic.text.primary }];

  return (
    <View style={desktopContentStyles.formInner}>
      <Text style={titleStyle}>Account verification</Text>

      <Text style={styles.description}>
        Please check your email to verify your account to proceed with setting your password.
      </Text>

      <View style={styles.buttonContainer}>
        <Pressable
          style={({ pressed }) => [
            styles.cancelButton,
            { borderColor: components.button.outlineBorder },
            Platform.OS === 'web' && cancelHovered && styles.cancelButtonHovered,
            Platform.OS === 'web' && cancelHovered && { backgroundColor: semantic.background.secondary },
            Platform.OS === 'web' && pressed && styles.cancelButtonPressed,
            Platform.OS === 'web' && pressed && { backgroundColor: semantic.background.tertiary },
          ]}
          onPress={handleCancel}
          disabled={isResending}
          onHoverIn={() => setCancelHovered(true)}
          onHoverOut={() => setCancelHovered(false)}
        >
          <Text style={[
            styles.cancelButtonText,
            { color: components.button.outlineText },
            Platform.OS === 'web' && cancelHovered && styles.cancelButtonTextHovered,
            Platform.OS === 'web' && cancelHovered && { color: semantic.text.primary },
          ]}>
            Cancel
          </Text>
        </Pressable>

        <Pressable
          style={({ pressed }) => [
            styles.resendButton,
            { borderColor: components.button.outlineBorder },
            isResending && styles.resendButtonDisabled,
            Platform.OS === 'web' && resendHovered && !isResending && styles.resendButtonHovered,
            Platform.OS === 'web' && resendHovered && !isResending && { backgroundColor: semantic.background.secondary },
            Platform.OS === 'web' && pressed && !isResending && styles.resendButtonPressed,
            Platform.OS === 'web' && pressed && !isResending && { backgroundColor: semantic.background.tertiary },
          ]}
          onPress={handleResendEmail}
          disabled={isResending}
          onHoverIn={() => setResendHovered(true)}
          onHoverOut={() => setResendHovered(false)}
        >
          <Text style={[
            styles.resendButtonText,
            { color: components.button.outlineText },
            Platform.OS === 'web' && resendHovered && !isResending && styles.resendButtonTextHovered,
            Platform.OS === 'web' && resendHovered && !isResending && { color: semantic.text.primary },
          ]}>
            Resend
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

const desktopContentStyles = StyleSheet.create({
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 40,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
});

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  // Base styles
  pageWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && { minHeight: '100vh' as any }),
  },
  pageWrapperAdmin: {
    backgroundColor: semantic.background.primary,
  },
  pageWrapperResident: {
    backgroundColor: semantic.background.primary,
  },
  
  // Header styles
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.secondary,
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 10,
      },
    }),
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    marginRight: 8,
    width: 64,
    height: 64,
  },
  logoText: {
    fontSize: Platform.select({ web: 48, default: 32 }),
    fontWeight: '600',
    color: semantic.text.primary,
    letterSpacing: 0.5,
  },

  // Layout styles
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: semantic.border.secondary,
    borderBottomWidth: Platform.select({ web: 1, default: 0 }),
    ...(Platform.OS === 'web' && { maxWidth: 1200 }),
    alignSelf: 'center',
    width: '100%',
  },
  splitContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  splitContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
  },

  // Image pane styles (matching LoginScreen.web.tsx)
  imagePane: {
    width: 512,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
      },
      default: {
        flex: 1,
        borderRightWidth: 1,
      },
    }),
  },
  imagePaneMobile: {
    display: 'none', // Hidden on mobile
  },
  imagePaneDesktop: {
    width: 512,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    flexShrink: 0,
  },
  image: {
    width: '100%',
    height: '100%',
    maxWidth: 512,
    objectFit: 'cover' as any,
  },

  formPane: {
    width: 512,
    alignItems: 'center',
    justifyContent: Platform.select({ web: 'center', default: 'flex-start' }),
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
      },
      default: {
        flex: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
      },
    }),
  },
  formPaneAdmin: {
    backgroundColor: '#171717',
  },
  formPaneResident: {
    backgroundColor: '#171717',
  },
  formPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
  },
  formPaneDesktop: {
    width: 512,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    justifyContent: 'center',
    flexShrink: 0,
  },

  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingVertical: Platform.select({ web: 0, default: 24 }),
    ...Platform.select({
      web: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    }),
  },
  formInnerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    maxWidth: '100%',
  },
  formInnerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 0,
    maxWidth: 512,
  },

  // Content styles
  title: {
    fontSize: Platform.select({ web: 24, default: 20 }),
    fontWeight: '700',
    textAlign: Platform.select({ web: 'left', default: 'center' }),
    marginBottom: 24,
    marginTop: 0,
  },
  titleAdmin: {
    color: '#eef1f4',
  },
  titleResident: {
    color: '#eef1f4',
  },
  titleMobile: {
    fontSize: 20,
    textAlign: 'center',
    marginBottom: 20,
  },
  titleDesktop: {
    fontSize: 24,
    textAlign: 'left',
    marginBottom: 24,
  },

  description: {
    fontSize: 14,
    color: semantic.text.secondary,
    lineHeight: 20,
    marginBottom: 32,
    textAlign: Platform.select({ web: 'left', default: 'center' }),
  },

  // Button styles (matching web apps Button component with colour="clear")
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 8,
  },
  cancelButton: {
    minWidth: 120,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
        '&:focus': {
          outline: 'none' as any,
          boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.5)' as any,
        },
      },
    }),
  },
  cancelButtonHovered: {
    backgroundColor: '#FFFFFF',
    borderColor: '#FFFFFF',
  },
  cancelButtonPressed: {
    backgroundColor: '#F3F4F6',
    borderColor: '#F3F4F6',
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  cancelButtonTextHovered: {
    color: '#374151',
  },
  resendButton: {
    minWidth: 120,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#FFFFFF',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        transition: 'all 0.2s ease-in-out' as any,
        '&:focus': {
          outline: 'none' as any,
          boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.5)' as any,
        },
      },
    }),
  },
  resendButtonHovered: {
    backgroundColor: '#FFFFFF',
    borderColor: '#FFFFFF',
  },
  resendButtonPressed: {
    backgroundColor: '#F3F4F6',
    borderColor: '#F3F4F6',
  },
  resendButtonDisabled: {
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
      },
    }),
  },
  resendButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },
  resendButtonTextHovered: {
    color: '#374151',
  },
  // Shared focus style for both buttons
  buttonFocused: {
    ...Platform.select({
      web: {
        boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.5)' as any,
      },
    }),
  },
});
